/**
 * @author        nico@zumper.com (Nico Rattazzi)
 * @copyright     Copyright (c) 2017, Zumper
 * @description   Container for messaging button
 */

import { Button, type ButtonProps } from '@zcode-ui/controls'
import { cn } from '@zcode-ui/shadz-ui'
import { selectIsSendingMessage } from '@zcode-ui/web-zumper-global-state'
import { selectIsMounted } from '@zcode-ui/web-zumper-global-state'
import { AnalyticsC, type Origin } from '@zcode/nr-utils'
import Listable from '@zcode/nr/es/shared/bizos/listable'
import Listing from '@zcode/nr/es/shared/bizos/listing'
import Pin from '@zcode/nr/es/shared/bizos/pin'
import {
  type APPLY,
  type MESSAGE as EQUAL_CTA_MESSAGE,
  TOUR,
  type WAITLIST,
} from '@zcode/nr/es/shared/constants/message'
import { oisEmpty } from '@zcode/nr/es/shared/utils/utils'
import type {
  Building,
  FloorplanListing,
  Listable as ListableType,
  Listing as ListingType,
  NormalListingData,
  Pin as PinType,
  Property,
  RawPinInventorySearch,
  SendMessageArgs,
} from '@zcode/state-type'
import { rootRelative } from '@zcode/utils'
import React, {
  type ForwardedRef,
  Fragment,
  type MouseEvent,
  forwardRef,
  useCallback,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { getSavedCustomMessage } from '../../features/SavedCustomMessage/module/thunks/getSavedCustomMessage'
import { selectFilterBedroom } from '../../modules/filters/selectors/index'
import { useDispatch } from '../../modules/useDispatch'
import { ExternalMessageButtonContainer } from '../ExternalMessageButton/ExternalMessageButtonContainer'
import { useAddNotification } from '../Notification/NotificationProvider'
import { getOneTapMessageData } from './getOneTapMessageData'
import { useStreamChannel } from './useStreamChannel'

export interface MessageButtonProps extends Pick<ButtonProps<undefined>, 'size' | 'variant'> {
  analyticChannel:
    | typeof AnalyticsC.FULL_DETAIL
    | typeof AnalyticsC.LIST
    | typeof AnalyticsC.LIST_ITEM
    | typeof AnalyticsC.LIST_ITEM_MESSAGED
    | typeof AnalyticsC.PERMANENT_ADDRESS
    | typeof AnalyticsC.PROGRAMMATIC_RENT
    | typeof AnalyticsC.HOMEPAGE
  className?: string
  dataTestId?: string
  disableOneTap?: boolean
  entity?: ListableType | Property | PinType<RawPinInventorySearch> | null | undefined
  equalCTAIntent?: typeof APPLY | typeof EQUAL_CTA_MESSAGE | typeof TOUR | typeof WAITLIST
  fetchMessagingEntity: (
    entity: ListingType | (ListableType & { isBuilding: true }) | PinType<RawPinInventorySearch>
  ) => Promise<ListingType<NormalListingData> | Building | null | undefined>
  hiddenText?: string
  isFetchingAnyTourIntegration: boolean
  isFetchingThisTourIntegration: boolean
  isFullWidth?: boolean
  isMessaged: boolean
  isMessagedText?: string
  onClick: (params: { messageEntity: ListableType | Property | null | undefined }) => void
  onlyShowFloorplanGroup?: boolean
  origin: Origin
  selectedFloorplan?: FloorplanListing
  sendMessage: (args: SendMessageArgs) => Promise<void>
  setSendingMessage: (isSendingMessage: boolean) => void
  showAgentInfo?: boolean
  /**
   * If true (or absent) AND the user has messaged the property, display the button differently:
   *
   *     1. Display "View message" text and link to the M3 conversation (if M3)
   *     2. Display "Messaged" and disable the button otherwise
   */
  showMessagedState?: boolean
  text?: string
  trackMessageClick: () => void
  trackUndoClick: () => void
  usePrimaryButton?: boolean
}

const MessageButton = forwardRef(
  (
    {
      className,
      dataTestId,
      disableOneTap,
      entity,
      fetchMessagingEntity,
      hiddenText,
      isFetchingAnyTourIntegration,
      isFetchingThisTourIntegration,
      isFullWidth,
      isMessaged,
      isMessagedText,
      onClick,
      text,
      usePrimaryButton,
      analyticChannel,
      equalCTAIntent,
      trackMessageClick,
      trackUndoClick,
      onlyShowFloorplanGroup,
      origin,
      selectedFloorplan,
      sendMessage,
      setSendingMessage,
      showAgentInfo,
      showMessagedState = true,
      variant,
      size,
    }: MessageButtonProps,
    ref: ForwardedRef<HTMLAnchorElement & HTMLButtonElement>
  ) => {
    // TODO: Move to separate hook if AB test is successful.
    const addNotification = useAddNotification()
    const dispatch = useDispatch()
    const isMounted = useSelector(selectIsMounted)

    const [oneTapLoading, setOneTapLoading] = useState(false)
    const bedroomsArray = useSelector(selectFilterBedroom)
    const isSendingMessage = useSelector(selectIsSendingMessage)
    const onClickSendMessageOrOpenForm = useCallback(
      async (event: React.MouseEvent) => {
        if (event) {
          event.preventDefault()
          event.stopPropagation()
        }
        const shouldFetchEntity =
          (isListing(entity) && !entity.hasNormalListingData) ||
          (entity instanceof Listable && entity?.isBuilding) ||
          isPin(entity)
        const messageEntity = shouldFetchEntity ? await fetchMessagingEntity(entity) : entity
        const oneTapMessageData = await dispatch(
          getOneTapMessageData({
            analyticChannel,
            bedroomsArray,
            disableOneTap,
            entity: messageEntity,
            equalCTAIntent,
            origin,
            selectedFloorplan,
          })
        )
        const isOneTapElegible = !!oneTapMessageData && !messageEntity?.isSpecialtyHousing
        if (!isOneTapElegible) {
          trackMessageClick()
          return onClick({ messageEntity })
        }
        setSendingMessage(true)
        setOneTapLoading(true)
        let undone = false
        const hasPhone = !!oneTapMessageData.messageData.phone

        const hideNotificationEarly = addNotification({
          message: hasPhone
            ? 'Sending message...'
            : `Sending message${entity?.headerText ? ` to ${entity.headerText}` : ''}`,
          bodyText: hasPhone
            ? `By providing your phone number, you consent (not a condition of purchase) to be called and texted by ${
                entity &&
                ('buildingName' in entity && !!entity.buildingName
                  ? entity.buildingName
                  : ((entity.isBuilding ? entity.name : entity.address) ?? 'this property'))
              }.`
            : undefined,
          onHide: async () => {
            if (undone) {
              return
            }
            const savedCustomMessage = dispatch(getSavedCustomMessage())
            if (savedCustomMessage) {
              oneTapMessageData.messageData.msg = savedCustomMessage
            }
            await sendMessage(oneTapMessageData)
            setOneTapLoading(false)
            setSendingMessage(false)
          },
          action: {
            label: 'Undo',
            onClick: () => {
              undone = true
              hideNotificationEarly()
              trackUndoClick()
              setOneTapLoading(false)
              setSendingMessage(false)
              onClick({ messageEntity })
            },
          },
          shouldTruncate: true,
        })
      },
      [
        addNotification,
        analyticChannel,
        bedroomsArray,
        dispatch,
        entity,
        equalCTAIntent,
        fetchMessagingEntity,
        trackMessageClick,
        trackUndoClick,
        onClick,
        onlyShowFloorplanGroup,
        origin,
        selectedFloorplan,
        sendMessage,
        setSendingMessage,
        showAgentInfo,
      ]
    )
    const { loading: loadingStreamChannelId, streamChannelId } = useStreamChannel(entity)

    if (oisEmpty(entity)) {
      return null
    }

    // TODO: the props say entity can be a ClusterItem, but ClusterItem doesn't have
    // isMessageable?
    const { externalUrl, feedName, isMessageable, isExternal } = entity
    let messageButtonText = text || 'Message'
    if (isMounted && showMessagedState && streamChannelId) {
      messageButtonText = 'View message'
    } else if (isMounted && showMessagedState && isMessaged) {
      messageButtonText = isMessagedText || 'Message Sent'
    }
    const onClickLink = (e: MouseEvent<HTMLAnchorElement>) => {
      e.stopPropagation()
    }

    const hiddenAndMessageText =
      isMounted && hiddenText ? (
        <Fragment>
          <p aria-hidden="true" className="invisible">
            {hiddenText}
          </p>
          <span className="absolute font-extrabold">{messageButtonText}</span>
        </Fragment>
      ) : (
        messageButtonText
      )

    if (isMessageable && !isExternal) {
      if (isMounted && showMessagedState && streamChannelId) {
        return (
          <Button
            as={Link}
            className={cn(isFullWidth ? 'w-full' : undefined, className)}
            to={`/messages/${streamChannelId}`}
            onClick={onClickLink}
            // @ts-expect-error
            ref={ref}
            size={size}
            variant={variant || 'secondary'}
          >
            {hiddenAndMessageText}
          </Button>
        )
      }
      return (
        <Button
          data-test-id={dataTestId}
          className={cn(isFullWidth ? 'w-full' : undefined, className)}
          disabled={
            (isMounted && showMessagedState && isMessaged) ||
            isSendingMessage ||
            (equalCTAIntent === TOUR && isFetchingAnyTourIntegration)
          }
          isLoading={
            (equalCTAIntent === TOUR && isFetchingThisTourIntegration) ||
            (isMounted && (loadingStreamChannelId || oneTapLoading))
          }
          onClick={onClickSendMessageOrOpenForm}
          ref={ref}
          size={size}
          variant={variant || (usePrimaryButton ? 'solid' : 'secondary')}
        >
          {hiddenAndMessageText}
        </Button>
      )
    }
    if (isExternal) {
      return (
        <ExternalMessageButtonContainer
          analyticChannel={AnalyticsC.MESSAGE}
          className={className}
          entity={entity}
          feedName={feedName}
          iconClassName={cn('relative', 'h-[17px]', '-top-px', 'min-[768px]:h-[15px]')}
          isFullWidth={isFullWidth ?? true}
          origin={origin}
          ref={ref}
          text={text}
          textClassName={cn('pl-1', 'hover:text-gray-0')}
          url={rootRelative(externalUrl)}
          variant={feedName ? 'pink' : 'solid'}
        />
      )
    }
    return null
  }
)

function isListing(entity: MessageButtonProps['entity']): entity is ListingType {
  return entity instanceof Listing
}

function isPin(entity: MessageButtonProps['entity']): entity is PinType<RawPinInventorySearch> {
  return entity instanceof Pin
}

export default MessageButton
